<template>
  <div :class="rootClasses">
    <label
      v-if="$slots.label"
      :for="attrs.id as string"
      :id="attrs.id ? `label-${attrs.id as string}` : undefined"
    >
      <slot name="label" />
    </label>

    <PVDropdown
      v-model="value"
      v-bind="bindAttrs"
      :aria-labelledby="attrs.id ? `label-${attrs.id as string}` : undefined"
      @focus="focused = true"
      @blur="focused = false"
      @mouseover="hovered = true"
      @mouseleave="hovered = false"
      ref="dropdown"
    >
      <template v-for="(_, slot) of $slots" v-slot:[slot]="scope">
        <slot v-if="slot" :name="slot" v-bind="scope" />
      </template>

      <template v-if="$slots.header && !customFilter" v-slot:header="{ options, value }">
        <slot name="header" :options="options" :value="value"></slot>
      </template>

      <template #dropdownicon>
        <i class="material-symbols-rounded"> expand_more </i>
      </template>
    </PVDropdown>

    <span v-if="$slots.helperText" class="caption">
      <slot name="helperText" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, useAttrs, computed } from 'vue';
import PVDropdown from 'primevue/dropdown';
import { useInputState } from '../composables';
import { merge } from 'lodash-es';

const props = defineProps({
  customFilter: Boolean,
  clearButtonAriaLabel: String,
});

const dropdown = ref<InstanceType<typeof PVDropdown>>();
const attrs = useAttrs();
const value = defineModel<any>();
const { focused, hovered, classes: rootClasses } = useInputState(attrs);

const bindAttrs = computed(() => {
  const { pt, ...rest } = attrs;

  const clearIconPt = props.clearButtonAriaLabel
    ? {
        'aria-label': props.clearButtonAriaLabel,
        role: 'button',
        tabindex: 0,
        'aria-hidden': false,
      }
    : undefined;

  const mergedPt = merge(
    {
      input: {
        'aria-required': attrs?.required,
        'aria-busy': attrs?.loading,
        disabled: attrs?.disabled,
        'aria-disabled': attrs?.disabled,
      },
      ...(clearIconPt ? { clearIcon: clearIconPt } : {}),
    },
    pt,
  );

  return { ...rest, pt: mergedPt };
});

defineExpose({ dropdown });
</script>
